export type ProductInitStatus = 'loading' | 'ready' | 'error';

type RemovableNode = {remove: () => void};

export type ProductInitDeps = {
    initStore: () => void;
    resetStore: () => void;
    removeLoader: () => void;
    logError: (error: unknown) => void;
    setStatus: (status: ProductInitStatus) => void;
};

export type ProductInitCoordinator = {
    run: () => ProductInitStatus;
    getStatus: () => ProductInitStatus;
    isInFlight: () => boolean;
};

const resolveGetElementById = (getElementById?: (id: string) => RemovableNode | null): ((id: string) => RemovableNode | null) => {
    if (getElementById) {
        return getElementById;
    }
    if (typeof document === 'undefined') {
        return () => null;
    }
    return (id: string) => document.getElementById(id) as RemovableNode | null;
};

/** Remove `#prod-loading`. A missing or already-removed node is a no-op. */
export const removeProdLoadingElement = (getElementById?: (id: string) => RemovableNode | null): void => {
    try {
        resolveGetElementById(getElementById)('prod-loading')?.remove();
    } catch {
        // Absent, already removed, or a host that cannot detach the node.
    }
};

export const createProductInitCoordinator = (deps: ProductInitDeps): ProductInitCoordinator => {
    let inFlight = false;
    let status: ProductInitStatus = 'loading';

    const setStatus = (next: ProductInitStatus) => {
        status = next;
        deps.setStatus(next);
    };

    const removeLoaderSafe = () => {
        try {
            deps.removeLoader();
        } catch {
            // A missing loader must not turn the original failure into a second error.
        }
    };

    const run = (): ProductInitStatus => {
        if (inFlight) {
            return status;
        }

        inFlight = true;
        // Keep the error UI mounted during retry so a blank #prod-v8 cannot flash
        // after the server loader has already been removed.
        if (status !== 'error') {
            setStatus('loading');
        }

        try {
            deps.resetStore();
            deps.initStore();
            setStatus('ready');
        } catch (error) {
            try {
                deps.logError(error);
            } catch {
                // Reporting must not replace the terminal error UI.
            }
            setStatus('error');
        } finally {
            removeLoaderSafe();
            inFlight = false;
        }

        return status;
    };

    return {
        run,
        getStatus: () => status,
        isInFlight: () => inFlight,
    };
};
